import React, { useState, useEffect } from 'react';
import { Tarefa, Usuario, StatusTarefa, Comentario } from '../../types';
import { useKanban } from '../../contexts/KanbanContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
// import { usuarios as todosUsuarios } from '../../data/mockData';
import tarefaService from '../../services/tarefaService';
import usuarioService from '../../services/usuarioService';
import { toast } from 'react-toastify';
import { mapearComentario } from '../tarefas/ComentarioMapper';
import AnexosTarefa from '../tarefas/AnexosTarefa';
import TempoResponsavel, { useRecarregarTempo } from '../tarefas/TempoResponsavel';
import { MIN_DIAS_UTEIS_POR_PRIORIDADE } from '../../utils/regrasPrazo';
import { useBackdropClose } from '../../hooks/useBackdropClose';

interface TaskDetailsModalProps {
  tarefa: Tarefa;
  aberto: boolean;
  onFechar: () => void;
  onDuplicar?: (tarefa: Tarefa) => void;
}

/**
 * Modal de detalhes da tarefa
 * 
 * Correções implementadas:
 * 1. Melhor tratamento de eventos para evitar propagação indesejada
 * 2. Funções específicas para fechamento do modal
 * 3. Correção do carregamento de comentários para evitar loops infinitos
 */
const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({ tarefa, aberto, onFechar, onDuplicar }) => {
  const { atualizarTarefa, recarregarTarefas } = useKanban();
  const { usuario } = useAuth();
  const { departamentoAtual, obterUsuariosDepartamento } = useDepartamento();
  const [novoComentario, setNovoComentario] = useState('');
  const [tarefaEditada, setTarefaEditada] = useState<Tarefa>(tarefa);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [usuariosDisponiveis, setUsuariosDisponiveis] = useState<Usuario[]>([]);
  const [mostrarSeletorResponsavel, setMostrarSeletorResponsavel] = useState(false);
  const [novoResponsavelId, setNovoResponsavelId] = useState<string>(tarefa?.responsavel?.id || '');
  const { key: tempoKey, recarregar: recarregarTempo } = useRecarregarTempo();
  const [definindoVencimento, setDefinindoVencimento] = useState(false);
  const [novoVencimento, setNovoVencimento] = useState('');
  const [salvandoVencimento, setSalvandoVencimento] = useState(false);

  // Carregar todos os usuários ativos do sistema para transferência
  useEffect(() => {
    const carregarUsuarios = async () => {
      if (tarefa) {
        try {
          // Carregar todos os usuários ativos do sistema
          const resposta = await usuarioService.obterTodos();
          if (resposta.success && resposta.usuarios) {
            const usuariosAtivos = resposta.usuarios
              .filter(u => u.ativo !== false)
              .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')); // Ordenação alfabética
            console.log('Todos os usuários ativos disponíveis para transferência:', usuariosAtivos);
            setUsuariosDisponiveis(usuariosAtivos);
          }
        } catch (error) {
          console.error('Erro ao carregar usuários para transferência:', error);
        }
      }
    };
    
    carregarUsuarios();
  }, [departamentoAtual, tarefa, obterUsuariosDepartamento]);
  
  // Efeito separado para inicializar o novoResponsavelId e atualizar a tarefa editada quando o modal é aberto
  useEffect(() => {
    if (aberto && tarefa) {
      setNovoResponsavelId(tarefa.responsavel?.id || '');
      setTarefaEditada(tarefa);
      
      // Verificar se a tarefa tem comentários
      if (!tarefa.comentarios || tarefa.comentarios.length === 0) {
        console.log('Tarefa sem comentários ou comentários não carregados, tentando recarregar');
        
        // Usar uma flag para evitar chamadas em loop
        const tarefaId = tarefa.id;
        
        // Tentar recarregar a tarefa para obter os comentários
        tarefaService.obterTarefa(tarefaId)
          .then(tarefaAtualizada => {
            if (tarefaAtualizada && tarefaAtualizada.comentarios) {
              console.log('Comentários recarregados com sucesso:', tarefaAtualizada.comentarios);
              // Atualizar a tarefa editada com os comentários carregados
              setTarefaEditada(prevState => {
                // Verificar se ainda estamos trabalhando com a mesma tarefa
                if (prevState.id === tarefaId) {
                  return {
                    ...prevState,
                    comentarios: tarefaAtualizada.comentarios
                  };
                }
                return prevState;
              });
            }
          })
          .catch(erro => {
            console.error('Erro ao recarregar comentários:', erro);
          });
      }
    }
  }, [aberto]); // Executa apenas quando o modal abre, removida a dependência de tarefa

  // Helper para transformar URLs em links clicáveis
  const renderDescricaoComLinks = (texto: string) => {
    const urlRegex = /((https?:\/\/|www\.)[\w\-]+(\.[\w\-]+)+(\S*)?)/gi;
    const html = (texto || '').replace(urlRegex, (match) => {
      const href = match.startsWith('http') ? match : `https://${match}`;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-bravo-primary underline">${match}</a>`;
    });
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // Fecha ao clicar no backdrop, mas não quando o clique é resultado de uma
  // seleção de texto (drag) iniciada dentro do modal e solta fora dele.
  // PRECISA vir antes de qualquer "return" condicional — hooks do React
  // têm que rodar sempre na mesma ordem em todo render.
  const backdropClose = useBackdropClose(onFechar);

  // Verificar se o modal está aberto e se a tarefa existe
  if (!aberto || !tarefa) return null;
  
  // REGRA: Título, descrição e prioridade NUNCA podem ser editados após criação
  // Nem criador, nem admin, NINGUÉM
  const podeEditarCamposBloqueados = false; // SEMPRE BLOQUEADO

  // A Data de Vencimento é exclusiva de quem RECEBEU o card — o criador
  // define a Data de Necessidade (na criação, sujeita à antecedência mínima
  // por prioridade); o responsável define o vencimento aqui livremente,
  // podendo inclusive antecipar a entrega. Sem trava de prazo mínimo aqui.
  const usuarioEhResponsavel = usuario?.id === tarefaEditada.responsavel?.id;
  const podeEditarData = usuarioEhResponsavel;
  const diasUteisMinimos = MIN_DIAS_UTEIS_POR_PRIORIDADE[tarefaEditada.prioridade] ?? MIN_DIAS_UTEIS_POR_PRIORIDADE.media;

  const handleSubmitComentario = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!novoComentario.trim() || !usuario || !tarefa?.id) return;
    
    // Salvar o texto do comentário antes de limpar
    const textoComentario = novoComentario;
    
    try {
      // Enviar o comentário para a API usando a função específica
      const response = await tarefaService.adicionarComentario(tarefa.id, textoComentario);
      
      console.log('Resposta da API ao adicionar comentário:', response);
      
      // Limpar campo imediatamente para melhor UX
      setNovoComentario('');
      
      // Criar comentário otimista para exibição imediata
      const comentarioOtimista: Comentario = {
        id: response.comentario?.id || `temp-${Date.now()}`,
        texto: textoComentario,
        dataCriacao: new Date().toISOString(),
        usuario: {
          id: usuario.id,
          nome: usuario.nome,
          email: usuario.email,
          cargo: usuario.cargo || '',
          departamentoId: usuario.departamentoId || '',
          tipo: usuario.tipo,
          avatar: "/profilepicture.png"
        }
      };
      
      // Atualizar estado local IMEDIATAMENTE
      setTarefaEditada(prev => ({
        ...prev,
        comentarios: [...(prev.comentarios || []), comentarioOtimista]
      }));
      
      // Mostrar toast de sucesso
      toast.success('Comentário adicionado!');
      
      // Recarregar tarefa em background para sincronizar
      setTimeout(async () => {
        try {
          const tarefaAtualizada = await tarefaService.obterTarefa(tarefa.id);
          if (tarefaAtualizada && tarefaAtualizada.comentarios) {
            console.log('Tarefa recarregada com comentários atualizados');
            setTarefaEditada(prev => ({
              ...prev,
              comentarios: tarefaAtualizada.comentarios
            }));
          }
        } catch (erro) {
          console.error('Erro ao recarregar comentários:', erro);
        }
      }, 500);
      
    } catch (error: any) {
      console.error('Erro ao adicionar comentário:', error);
      
      // Verificar se o comentário foi criado apesar do erro
      // (problema conhecido: backend retorna erro mas cria o comentário)
      setTimeout(async () => {
        try {
          const tarefaAtualizada = await tarefaService.obterTarefa(tarefa.id);
          if (tarefaAtualizada && tarefaAtualizada.comentarios) {
            const comentarioFoiCriado = tarefaAtualizada.comentarios.some(
              (c: Comentario) => c.texto === textoComentario && c.usuario?.id === usuario.id
            );
            
            if (comentarioFoiCriado) {
              console.log('✅ Comentário foi criado com sucesso apesar do erro na resposta');
              toast.success('Comentário adicionado!');
              setNovoComentario(''); // Limpar campo
              setTarefaEditada(prev => ({
                ...prev,
                comentarios: tarefaAtualizada.comentarios
              }));
            } else {
              toast.error('Erro ao adicionar comentário. Tente novamente.');
              setNovoComentario(textoComentario);
            }
          }
        } catch (erro) {
          console.error('Erro ao verificar comentário:', erro);
          toast.error('Erro ao adicionar comentário. Tente novamente.');
          setNovoComentario(textoComentario);
        }
      }, 1000);
    }
  };

  const handleSalvarEdicao = () => {
    // atualizarTarefa já mostra toast com a mensagem de erro real e
    // recarrega as tarefas em caso de falha (ver KanbanContext) — o catch
    // aqui só evita um "unhandled promise rejection" no console.
    atualizarTarefa(tarefaEditada).catch(() => {});
    setModoEdicao(false);
    recarregarTempo();
  };

  // Ação rápida e sempre visível para o responsável definir o vencimento
  // pela primeira vez, sem precisar entrar no modo de edição geral do card
  // (que também mexe em outros campos e pode confundir).
  const handleDefinirVencimento = async () => {
    if (!novoVencimento || !tarefa?.id) return;
    setSalvandoVencimento(true);
    try {
      const response = await tarefaService.atualizarTarefa(tarefa.id, {
        dataVencimento: novoVencimento
      } as Partial<Tarefa>);

      if (response.success) {
        // Ancora ao meio-dia antes de guardar no estado local — uma string
        // "YYYY-MM-DD" pura é interpretada como meia-noite UTC pelo
        // `new Date()` do formatarData, o que exibia o dia anterior em
        // fusos negativos (ex: Brasil). Mesmo truque já usado no backend.
        setTarefaEditada(prev => ({ ...prev, dataVencimento: `${novoVencimento}T12:00:00` }));
        setDefinindoVencimento(false);
        setNovoVencimento('');
        toast.success('Data de vencimento definida com sucesso!');
        recarregarTempo();
        // Sincroniza o quadro Kanban inteiro — sem isso, o card no board
        // ficava com o vencimento antigo (null) em memória, e um
        // drag-and-drop logo em seguida mandava esse valor desatualizado
        // de volta pro servidor, apagando o vencimento recém-definido.
        recarregarTarefas();
      } else {
        toast.error(response.message || 'Erro ao definir data de vencimento');
      }
    } catch (error: any) {
      console.error('Erro ao definir vencimento:', error);
      toast.error(error?.response?.data?.message || 'Erro ao definir data de vencimento');
    } finally {
      setSalvandoVencimento(false);
    }
  };

  const handleTransferirResponsavel = async () => {
    if (novoResponsavelId === tarefa?.responsavel?.id) return;
    
    // Encontrar o usuário selecionado nos usuários disponíveis
    const novoResponsavel = usuariosDisponiveis.find(u => u.id === novoResponsavelId);
    if (!novoResponsavel) return;
    
    try {
      // Mostrar feedback visual
      toast.info(`Transferindo responsabilidade para ${novoResponsavel.nome}...`);
      
      // Obter o departamento do novo responsável
      // Garantir que o departamento_id não seja undefined
      const novoDepartamentoId = novoResponsavel.departamento_id || '';
      const novoDepartamentoNome = novoResponsavel.departamento_nome || '';
      
      console.log('Departamento do novo responsável:', novoDepartamentoNome, '(ID:', novoDepartamentoId, ')');
      
      // Para transferência de responsabilidade, enviar apenas os campos necessários
      // O backend detectará automaticamente que é uma transferência e definirá o status como "Solicitado"
      const dadosTransferencia = {
        responsavel_id: novoResponsavelId,
        departamento_id: novoDepartamentoId
      };
      
      console.log('Transferindo responsabilidade para:', novoResponsavel.nome, '(ID:', novoResponsavelId, ')');
      console.log('Atualizando departamento para:', novoDepartamentoNome, '(ID:', novoDepartamentoId, ')');
      console.log('Dados enviados para transferência:', dadosTransferencia);
      
      // Enviar apenas os dados de transferência para o servidor
      await tarefaService.atualizarTarefa(tarefa.id, dadosTransferencia);
      
      // Atualizar estado local após sucesso
      const tarefaAtualizada = {
        ...tarefaEditada,
        responsavel: novoResponsavel,
        responsavel_id: novoResponsavelId,
        departamento_id: novoDepartamentoId,
        status: 'Solicitado' as StatusTarefa // O backend definirá como "Solicitado"
      };
      setTarefaEditada(tarefaAtualizada);
      
      // Fechar o seletor apenas após a atualização ser concluída com sucesso
      setMostrarSeletorResponsavel(false);
      recarregarTempo();
      toast.success(`Responsabilidade transferida para ${novoResponsavel.nome}`);
    } catch (error) {
      console.error('Erro ao transferir responsabilidade:', error);
      toast.error('Erro ao transferir responsabilidade. Tente novamente.');
      // Não fechamos o seletor em caso de erro para permitir nova tentativa
    }
  };

  const handleCancelarEdicao = () => {
    setTarefaEditada(tarefa);
    setModoEdicao(false);
  };

  const formatarData = (dataString: string | null) => {
    if (!dataString) return 'Sem data';
    
    const data = new Date(dataString);
    return format(data, "dd 'de' MMM", { locale: ptBR });
  };

  const getPrioridadeCor = (prioridade: string) => {
    switch (prioridade.toLowerCase()) {
      case 'baixa':
        return 'bg-teal-50 dark:bg-teal-950 text-priority-baixa border-priority-baixa';
      case 'media':
      case 'média':
        return 'bg-amber-50 dark:bg-amber-950 text-priority-media border-priority-media';
      case 'alta':
        return 'bg-orange-50 dark:bg-orange-950 text-priority-alta border-priority-alta';
      case 'urgente':
        return 'bg-red-50 dark:bg-red-950 text-priority-urgente dark:text-red-300 border-priority-urgente';
      default:
        return 'bg-gray-100 text-bravo-secondary border-gray-300';
    }
  };

  // Função específica para o botão de fechar
  const handleCloseButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir propagação do evento
    onFechar();
  };

  // Função para duplicar tarefa
  const handleDuplicarTarefa = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDuplicar) {
      onDuplicar(tarefaEditada);
      onFechar(); // Fechar o modal atual
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      {...backdropClose}
    >
      <div className="bg-white rounded-lg shadow-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-gray-200 flex justify-between items-center">
          {modoEdicao ? (
            <div className="flex-1">
              <input
                type="text"
                value={tarefaEditada.titulo}
                onChange={(e) => setTarefaEditada({ ...tarefaEditada, titulo: e.target.value })}
                disabled={!podeEditarCamposBloqueados}
                className={`text-xl font-bold w-full border-b ${
                  !podeEditarCamposBloqueados 
                    ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' 
                    : 'border-gray-300'
                } focus:outline-none focus:border-bravo-primary`}
              />
              {!podeEditarCamposBloqueados && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  🔒 Este campo não pode ser alterado após a criação da tarefa
                </p>
              )}
            </div>
          ) : (
            <h2 className="text-xl font-bold text-bravo-dark">{tarefaEditada.titulo}</h2>
          )}
          
          <button 
            onClick={handleCloseButtonClick}
            className="text-bravo-secondary hover:text-bravo-primary rounded-full p-1 hover:bg-bravo-tertiary transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex-grow overflow-y-auto p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Status</h3>
              {modoEdicao ? (
                <select
                  value={tarefaEditada.status}
                  onChange={(e) => setTarefaEditada({ ...tarefaEditada, status: e.target.value as StatusTarefa })}
                  className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary"
                >
                  <option value="Pendente">Pendente</option>
                  <option value="A iniciar">A iniciar</option>
                  <option value="Em Andamento">Em Andamento</option>
                  <option value="Aguardando Aprovação">Aguardando Aprovação</option>
                  <option value="Concluída">Concluída</option>
                </select>
              ) : (
                <div className="bg-bravo-tertiary text-bravo-secondary inline-block px-3 py-1 rounded-full text-sm font-medium">
                  {tarefaEditada.status}
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Prioridade</h3>
              {modoEdicao ? (
                <div>
                  <select
                    value={tarefaEditada.prioridade}
                    onChange={(e) => setTarefaEditada({ 
                      ...tarefaEditada, 
                      prioridade: e.target.value as 'baixa' | 'media' | 'alta' | 'urgente' 
                    })}
                    disabled={!podeEditarCamposBloqueados}
                    className={`w-full p-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary ${
                      !podeEditarCamposBloqueados 
                        ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' 
                        : 'border-gray-200'
                    }`}
                  >
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                  {!podeEditarCamposBloqueados && (
                    <p className="text-xs text-red-600 mt-1 font-medium">🔒 Bloqueado permanentemente</p>
                  )}
                </div>
              ) : (
                <div
                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getPrioridadeCor(tarefaEditada.prioridade)}`}
                  title={`Prazo mínimo de ${MIN_DIAS_UTEIS_POR_PRIORIDADE[(tarefaEditada.prioridade || '').toLowerCase()] ?? MIN_DIAS_UTEIS_POR_PRIORIDADE.media} dia(s) útil(eis) de antecedência para essa prioridade`}
                >
                  {tarefaEditada.prioridade.charAt(0).toUpperCase() + tarefaEditada.prioridade.slice(1)}
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Data de Criação</h3>
              <div className="text-sm font-medium text-bravo-secondary">
                {formatarData(tarefaEditada.dataCriacao)}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Data de Necessidade</h3>
              <div className="text-sm font-medium text-bravo-secondary">
                {tarefaEditada.dataNecessidade ? formatarData(tarefaEditada.dataNecessidade) : 'Não informada'}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Data de Vencimento</h3>
              {modoEdicao && podeEditarData ? (
                <>
                  <input
                    type="date"
                    value={tarefaEditada.dataVencimento || ''}
                    onChange={(e) => setTarefaEditada({ ...tarefaEditada, dataVencimento: e.target.value })}
                    className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary"
                  />
                </>
              ) : tarefaEditada.dataVencimento ? (
                <div className="text-sm font-medium text-bravo-secondary">
                  {formatarData(tarefaEditada.dataVencimento)}
                </div>
              ) : podeEditarData ? (
                // Sem modo de edição geral: ação rápida e sempre visível
                // para quem pode definir o vencimento (evita depender do
                // botão genérico "Editar Tarefa", pouco intuitivo pra essa
                // ação específica). A regra de prazo mínimo fica visível
                // já na abertura do card, antes mesmo de clicar em nada.
                <div>
                  {!definindoVencimento ? (
                    <button
                      type="button"
                      onClick={() => setDefinindoVencimento(true)}
                      className="text-sm font-medium text-bravo-primary hover:underline"
                    >
                      + Definir data de vencimento
                    </button>
                  ) : (
                    <div>
                      <input
                        type="date"
                        value={novoVencimento}
                        onChange={(e) => setNovoVencimento(e.target.value)}
                        autoFocus
                        className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary"
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          onClick={handleDefinirVencimento}
                          disabled={!novoVencimento || salvandoVencimento}
                          className="px-3 py-1.5 bg-bravo-primary text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {salvandoVencimento ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setDefinindoVencimento(false); setNovoVencimento(''); }}
                          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-bravo-secondary hover:bg-bravo-tertiary transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm font-medium text-bravo-secondary">
                  Ainda não definida pelo responsável
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-8">
            <h3 className="text-sm font-medium text-bravo-secondary mb-2">Descrição</h3>
            {modoEdicao ? (
              <div>
                <textarea
                  value={tarefaEditada.descricao}
                  onChange={(e) => setTarefaEditada({ ...tarefaEditada, descricao: e.target.value })}
                  disabled={!podeEditarCamposBloqueados}
                  className={`w-full p-3 border rounded-md min-h-[120px] focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary ${
                    !podeEditarCamposBloqueados 
                      ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600' 
                      : 'border-gray-200'
                  }`}
                  placeholder="Descrição da tarefa"
                />
                {!podeEditarCamposBloqueados && (
                  <p className="text-xs text-red-600 mt-1 font-medium">
                    🔒 Este campo não pode ser alterado após a criação. Use os comentários para adicionar informações.
                  </p>
                )}
              </div>
            ) : (
              <div className="text-bravo-secondary whitespace-pre-wrap bg-bravo-tertiary p-4 rounded-md">
                {renderDescricaoComLinks(tarefaEditada.descricao)}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Responsável</h3>
              {modoEdicao ? (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <select
                    value={tarefaEditada.responsavel?.id || ''}
                    onChange={(e) => {
                      const novoRespId = e.target.value;
                      const novoResp = usuariosDisponiveis.find(u => u.id === novoRespId);
                      if (novoResp) {
                        setTarefaEditada({ ...tarefaEditada, responsavel: novoResp });
                      }
                    }}
                    className="w-full p-2 focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary"
                  >
                    {usuariosDisponiveis.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.nome} - {u.cargo}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-xs text-bravo-secondary">Responsável</p>
                      <p className="text-sm font-semibold text-bravo-dark">{tarefaEditada.responsavel?.nome || 'Não atribuído'}</p>
                    </div>
                  </div>

                  {(usuario?.tipo === 'admin' || usuario?.tipo === 'comum') && (
                    <div className="mt-3 space-y-2">
                      {!mostrarSeletorResponsavel ? (
                        <button
                          type="button"
                          onClick={() => setMostrarSeletorResponsavel(true)}
                          className="text-sm font-semibold text-bravo-primary hover:underline"
                        >
                          Transferir
                        </button>
                      ) : (
                        <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-xs text-bravo-secondary font-medium">Selecione o novo responsável</label>
                            <button
                              type="button"
                              onClick={() => setMostrarSeletorResponsavel(false)}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Cancelar
                            </button>
                          </div>
                          <select
                            className="w-full p-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary mb-2"
                            value={novoResponsavelId}
                            onChange={(e) => setNovoResponsavelId(e.target.value)}
                          >
                            <option value="">Selecione um responsável</option>
                            {usuariosDisponiveis.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.nome} ({u.departamento_nome || u.departamento_id || 'sem depto'})
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={handleTransferirResponsavel}
                            disabled={!novoResponsavelId || novoResponsavelId === tarefa?.responsavel?.id}
                            className={`w-full py-2 rounded-md text-white font-semibold transition-colors ${
                              !novoResponsavelId || novoResponsavelId === tarefa?.responsavel?.id
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-bravo-primary hover:bg-bravo-primary/90'
                            }`}
                          >
                            Confirmar transferência
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-bravo-secondary mb-2">Criado por</h3>
              <div className="flex items-center p-3 border border-gray-200 rounded-md">
                <img 
                  src="/profilepicture.png" 
                  alt={tarefaEditada.criador?.nome || 'Desconhecido'}
                  className="w-10 h-10 rounded-full mr-3 border-2 border-gray-200"
                  title={tarefaEditada.criador?.nome || 'Desconhecido'}
                />
                <div>
                  <p className="text-sm font-medium text-bravo-dark">{tarefaEditada.criador?.nome || 'Desconhecido'}</p>
                  <p className="text-xs text-bravo-secondary">{tarefaEditada.criador?.cargo || ''}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Seção de Anexos */}
          <div className="mt-6 mb-6">
            <AnexosTarefa 
              tarefaId={tarefa.id} 
              podeEditar={usuario?.id === tarefa.criador?.id || usuario?.id === tarefa.responsavel?.id || usuario?.tipo === 'admin'} 
            />
          </div>

          {/* Seção de Histórico de Transferências */}
          {tarefaEditada.texto_transferencia && (
            <div className="mt-6 mb-6">
              <h3 className="text-sm font-medium text-bravo-secondary mb-3">Histórico de Transferências</h3>
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                <pre className="whitespace-pre-wrap text-sm text-bravo-dark font-mono">
                  {tarefaEditada.texto_transferencia}
                </pre>
              </div>
            </div>
          )}

          {/* Temporizador por responsável — visível apenas para admin e líderes de departamento */}
          {(usuario?.tipo === 'admin' || usuario?.ehLider || usuario?.eh_lider) && (
            <TempoResponsavel key={tempoKey} tarefaId={tarefa.id} tarefaStatus={tarefaEditada.status} dataVencimento={tarefaEditada.dataVencimento} />
          )}
          
          <div>
            <h3 className="text-sm font-medium text-bravo-secondary mb-3">Comentários ({tarefaEditada.comentarios?.length || 0})</h3>
            <div className="space-y-4">
              {tarefaEditada.comentarios?.map((comentario) => (
                <div key={comentario.id} className="bg-bravo-tertiary p-4 rounded-md border border-gray-100">
                  <div className="flex items-center mb-3">
                    <img 
                      src="/profilepicture.png" 
                      alt={comentario.usuario?.nome || 'Usuário'}
                      className="w-8 h-8 rounded-full mr-3 border border-gray-200"
                      title={comentario.usuario?.nome || 'Usuário'}
                    />
                    <div>
                      <p className="text-sm font-medium text-bravo-dark">{comentario.usuario?.nome || 'Usuário'}</p>
                      <p className="text-xs text-bravo-secondary">{formatarData(comentario.dataCriacao)}</p>
                    </div>
                  </div>
                  <p className="text-sm text-bravo-secondary whitespace-pre-wrap">{comentario.texto}</p>
                </div>
              ))}
              
              <form onSubmit={handleSubmitComentario} className="mt-6">
                <div className="flex flex-col">
                  <textarea
                    value={novoComentario}
                    onChange={(e) => setNovoComentario(e.target.value)}
                    placeholder="Adicionar um comentário..."
                    className="w-full p-3 border border-gray-200 rounded-t-md focus:outline-none focus:ring-1 focus:ring-bravo-primary focus:border-bravo-primary min-h-[100px] resize-y"
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="bg-bravo-primary text-white px-5 py-2 rounded-b-md hover:bg-red-700 transition-colors font-medium w-full md:w-auto"
                    >
                      Enviar Comentário
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <div className="p-5 border-t border-gray-200 flex justify-between">
          {modoEdicao ? (
            <>
              <button
                onClick={handleCancelarEdicao}
                className="px-5 py-2.5 border border-gray-300 rounded-md text-bravo-secondary hover:bg-bravo-tertiary transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSalvarEdicao}
                className="px-5 py-2.5 bg-bravo-primary text-white rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                Salvar Alterações
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseButtonClick}
                  className="px-5 py-2.5 border border-gray-300 rounded-md text-bravo-secondary hover:bg-bravo-tertiary transition-colors font-medium"
                >
                  Fechar
                </button>
                {onDuplicar && (
                  <button
                    onClick={handleDuplicarTarefa}
                    className="px-5 py-2.5 border border-blue-500 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors font-medium flex items-center"
                    title="Duplicar esta tarefa"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                      <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                    </svg>
                    Duplicar
                  </button>
                )}
              </div>
              <button
                onClick={() => setModoEdicao(true)}
                className="px-5 py-2.5 bg-bravo-primary text-white rounded-md hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Editar Tarefa
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;
